import { DASHBOARD_SCHEMA_VERSION } from '@zeroth/shared';

import { apiClient } from './api';

export const PAGE_LOAD_DIAGNOSTIC_PENDING_KEY = 'zeroth:page-load-diagnostic:pending';
export const PAGE_LOAD_DIAGNOSTIC_SENT_KEY = 'zeroth:page-load-diagnostic:sent';
export const PAGE_LOAD_RECOVERY_KEY = 'zeroth:page-load-recovery';

const MAX_MESSAGE_LENGTH = 500;
const MAX_STACK_LENGTH = 2_000;
const MAX_COMPONENT_STACK_LENGTH = 1_000;
const MAX_SENT_FINGERPRINTS = 50;

export type PageLoadFailureCategory = 'asset' | 'render' | 'data_contract';

export interface PageLoadDiagnostic {
  route: string;
  frontendSha: string;
  dashboardSchemaVersion: number;
  category: PageLoadFailureCategory;
  errorName: string;
  message: string;
  stack: string | null;
  componentStack: string | null;
  assetPath: string | null;
  recoveryAttempt: number;
  fingerprint: string;
}

interface QueuePageLoadDiagnosticInput {
  error: Error;
  componentStack: string;
  route: string;
  recoveryAttempt: number;
  category?: PageLoadFailureCategory;
  storage?: Storage;
}

const ASSET_ERROR_PATTERN = /(?:dynamically imported module|importing a module script failed|chunkloaderror|loading chunk|failed to fetch module script|vite:preloaderror)/i;

function getSessionStorage(override?: Storage): Storage | null {
  if (override) return override;
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function sanitizeRoute(route: string): string {
  const path = route.split('?')[0].split('#')[0];
  return path.startsWith('/') ? path.slice(0, 256) : '/';
}

function replaceControlCharacters(value: string): string {
  return [...value]
    .map((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint <= 31 || codePoint === 127 ? ' ' : character;
    })
    .join('');
}

function redactSensitiveText(value: string): string {
  return value
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [REDACTED]')
    .replace(/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g, '[REDACTED_JWT]')
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[REDACTED_EMAIL]')
    .replace(
      /\b(token|code|secret|password|authorization|api[-_]?key|apikey|printToken|spapi_oauth_code|state)\b(\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^\s,;)\]}]+)/gi,
      '$1$2[REDACTED]',
    );
}

function sanitizeText(value: string | undefined, maxLength: number): string | null {
  if (!value) return null;
  return redactSensitiveText(replaceControlCharacters(value))
    .replace(/\?[^\s)]*/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength) || null;
}

export function getFrontendReleaseSha(): string {
  const sha = import.meta.env.VITE_APP_RELEASE_SHA;
  return sha && /^[0-9a-f]{40}$/.test(sha) ? sha : 'development';
}

function hashFingerprint(value: string): string {
  let left = 0x811c9dc5;
  let right = 0x9e3779b9;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    left = Math.imul(left ^ code, 0x01000193);
    right = Math.imul(right ^ code, 0x85ebca6b);
  }
  return `${(left >>> 0).toString(16).padStart(8, '0')}${(right >>> 0).toString(16).padStart(8, '0')}`;
}

function readSentFingerprints(storage: Storage): string[] {
  try {
    const parsed = JSON.parse(storage.getItem(PAGE_LOAD_DIAGNOSTIC_SENT_KEY) ?? '[]');
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === 'string').slice(-MAX_SENT_FINGERPRINTS)
      : [];
  } catch {
    return [];
  }
}

function findAssetPath(error: Error): string | null {
  if (typeof window === 'undefined') return null;
  const source = `${error.message}\n${error.stack ?? ''}`;
  const match = source.match(/(?:https?:\/\/[^\s)]+|\/assets\/[^\s)]+)/i)?.[0];
  if (!match) return null;
  try {
    const url = new URL(match, window.location.origin);
    return url.origin === window.location.origin ? url.pathname.slice(0, 512) : null;
  } catch {
    return null;
  }
}

export function classifyPageLoadError(error: Error): PageLoadFailureCategory {
  return ASSET_ERROR_PATTERN.test(`${error.name}: ${error.message}`) ? 'asset' : 'render';
}

export function createPageLoadDiagnostic(input: QueuePageLoadDiagnosticInput): PageLoadDiagnostic {
  const route = sanitizeRoute(input.route);
  const category = input.category ?? classifyPageLoadError(input.error);
  const errorName = sanitizeText(input.error.name, 80) ?? 'Error';
  const message = sanitizeText(input.error.message, MAX_MESSAGE_LENGTH) ?? 'Unknown page-load failure';
  const stack = sanitizeText(input.error.stack, MAX_STACK_LENGTH);
  const componentStack = sanitizeText(input.componentStack, MAX_COMPONENT_STACK_LENGTH);
  const frontendSha = getFrontendReleaseSha();
  const fingerprint = hashFingerprint([category, route, frontendSha, errorName, message].join('|'));

  return {
    route,
    frontendSha,
    dashboardSchemaVersion: DASHBOARD_SCHEMA_VERSION,
    category,
    errorName,
    message,
    stack,
    componentStack,
    assetPath: category === 'asset' ? findAssetPath(input.error) : null,
    recoveryAttempt: Math.max(0, Math.min(1, Math.trunc(input.recoveryAttempt))),
    fingerprint,
  };
}

export function queuePageLoadDiagnostic(input: QueuePageLoadDiagnosticInput): boolean {
  const storage = getSessionStorage(input.storage);
  if (!storage) return false;
  const diagnostic = createPageLoadDiagnostic(input);
  const sent = readSentFingerprints(storage);
  if (sent.includes(diagnostic.fingerprint)) return false;

  try {
    storage.setItem(PAGE_LOAD_DIAGNOSTIC_PENDING_KEY, JSON.stringify(diagnostic));
    storage.setItem(
      PAGE_LOAD_DIAGNOSTIC_SENT_KEY,
      JSON.stringify([...sent, diagnostic.fingerprint].slice(-MAX_SENT_FINGERPRINTS)),
    );
    return true;
  } catch {
    return false;
  }
}

export async function flushPendingPageLoadDiagnostic(
  storageOverride?: Storage,
): Promise<boolean> {
  const storage = getSessionStorage(storageOverride);
  if (!storage) return false;
  let diagnostic: PageLoadDiagnostic;
  try {
    const raw = storage.getItem(PAGE_LOAD_DIAGNOSTIC_PENDING_KEY);
    if (!raw) return false;
    diagnostic = JSON.parse(raw) as PageLoadDiagnostic;
  } catch {
    storage.removeItem(PAGE_LOAD_DIAGNOSTIC_PENDING_KEY);
    return false;
  }

  try {
    await apiClient.post('/api/client-diagnostics/page-load', diagnostic);
    storage.removeItem(PAGE_LOAD_DIAGNOSTIC_PENDING_KEY);
    return true;
  } catch {
    return false;
  }
}

export function claimPageLoadRecovery(
  diagnostic: PageLoadDiagnostic,
  storageOverride?: Storage,
): boolean {
  const storage = getSessionStorage(storageOverride);
  if (!storage) return false;
  const key = `${diagnostic.route}:${diagnostic.frontendSha}:${diagnostic.fingerprint}`;
  try {
    const stored = JSON.parse(storage.getItem(PAGE_LOAD_RECOVERY_KEY) ?? '[]');
    const claimed = Array.isArray(stored)
      ? stored.filter((value): value is string => typeof value === 'string').slice(-49)
      : [];
    if (claimed.includes(key)) return false;
    storage.setItem(PAGE_LOAD_RECOVERY_KEY, JSON.stringify([...claimed, key]));
    return true;
  } catch {
    try {
      storage.setItem(PAGE_LOAD_RECOVERY_KEY, JSON.stringify([key]));
      return true;
    } catch {
      return false;
    }
  }
}
